import { useState } from "react";
import { Modal, Form, Input, DatePicker, Button, Checkbox } from "antd";
import { CheckCircleOutlined, FileTextOutlined, EyeOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import SignaturePad from "../../../../../components/Shared/SignaturePad";
import uploadFile from "../../../../../utilities/uploadFile";
import { useToast } from "../../../../../contexts/ToastContext";

const TeqMobilityConnectionModal = ({ open, onCancel, onComplete, loading: externalLoading }) => {
    const toast = useToast();
    const [form] = Form.useForm();
    const [signatureFile, setSignatureFile] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [uploading, setUploading] = useState(false);

    const loading = externalLoading || uploading;

    const [requiredDocuments, setRequiredDocuments] = useState([
        {
            id: 1,
            title: "SOFTCARTECH B2B ACCESS PROTECTION PLAN GUIDE",
            reviewed: false,
            pdfUrl: "https://storage.googleapis.com/dronemobile-carapp.appspot.com/uploads/41580f5b-3aca-4cd3-a31c-06dc7f32893f.pdf", // Replace with actual URL
        },
        {
            id: 2,
            title: "TeqMobility Insurance Overview",
            reviewed: false,
            pdfUrl: "https://storage.googleapis.com/dronemobile-carapp.appspot.com/uploads/438e28e8-8135-4106-b5f5-fd6a908d2b25.pdf", // Replace with actual URL
        },
        {
            id: 3,
            title: "TeqMobility New User Agreement",
            reviewed: false,
            pdfUrl: "https://storage.googleapis.com/dronemobile-carapp.appspot.com/uploads/26d58796-2535-45a8-bb7d-1092f7affeaf.pdf", // Replace with actual URL
        },
    ]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!agreed) {
                return;
            }

            if (!signatureFile) {
                return;
            }

            setUploading(true);

            // Upload signature file and get URL
            let signatureUrl;
            try {
                signatureUrl = await uploadFile(signatureFile, "Signatures");
            } catch (_uploadError) {
                toast.error({
                    message: "Error uploading signature",
                });
                setUploading(false);
                return;
            }

            // Validate that we got a URL
            if (!signatureUrl || typeof signatureUrl !== "string") {
                toast.error({
                    message: "Error uploading signature",
                });
                setUploading(false);
                return;
            }

            // Prepare payload for API with signature URL
            const payload = {
                agreementpdf1: requiredDocuments[0].pdfUrl,
                agreementpdf2: requiredDocuments[1].pdfUrl,
                agreementpdf3: requiredDocuments[2].pdfUrl,
                name: values.fullName,
                email: values.email,
                date: values.date?.format("YYYY-MM-DD"),
                phone_number: values.contactNumber,
                signature: signatureUrl,
            };

            onComplete(payload);
            setUploading(false);
        } catch (error) {
            // Validation failed - form will show errors
            setUploading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setSignatureFile(null);
        setAgreed(false);

        // Reset all documents to not reviewed
        setRequiredDocuments((prevDocs) =>
            prevDocs.map((doc) => ({
                ...doc,
                reviewed: false,
            })),
        );

        onCancel();
    };

    const handleSignatureSave = (file) => {
        setSignatureFile(file);
    };

    const handleSignatureClear = () => {
        setSignatureFile(null);
    };

    const handleViewDocument = (document) => {
        setSelectedDocument(document);
        setPdfModalOpen(true);
    };

    const handleClosePdfModal = () => {
        setPdfModalOpen(false);
        setSelectedDocument(null);
    };

    const handleAgreementChange = (e) => {
        const isChecked = e.target.checked;
        setAgreed(isChecked);

        // Update all documents' reviewed status based on checkbox
        setRequiredDocuments((prevDocs) =>
            prevDocs.map((doc) => ({
                ...doc,
                reviewed: isChecked,
            })),
        );
    };

    return (
        <>
            <Modal
                open={open}
                onCancel={handleCancel}
                footer={null}
                width={600}
                centered
                title={
                    <div className="flex items-center gap-2">
                        <FileTextOutlined className="text-blue-600" />
                        <span> Teqmobility Insurance Terms and Conditions</span>
                    </div>
                }
                maskClosable={!loading}
                closable={!loading}
                styles={{
                    body: {
                        maxHeight: "80vh",
                        overflowY: "auto",
                        padding: "16px 0",
                    },
                }}>
                <div>
                    {/* Review Required Documents Section */}
                    <div className="px-4">
                        <div className="flex flex-col gap-3">
                            {requiredDocuments.map((doc) => (
                                <div
                                    key={doc.id}
                                    className={`border-2 rounded-lg p-4 flex items-center justify-between cursor-pointer transition-colors ${
                                        doc.reviewed
                                            ? "border-green-400 bg-green-50 hover:border-green-500"
                                            : "border-gray-300 bg-gray-50 hover:border-gray-400"
                                    }`}
                                    onClick={() => handleViewDocument(doc)}>
                                    <div className="flex items-center gap-3">
                                        <FileTextOutlined
                                            className={`text-xl ${doc.reviewed ? "text-green-600" : "text-gray-400"}`}
                                        />
                                        <div>
                                            <p className="font-medium text-gray-800">{doc.title}</p>
                                            <p
                                                className={`text-sm ${doc.reviewed ? "text-gray-600" : "text-gray-400"}`}>
                                                {doc.reviewed ? "Document reviewed" : "Not reviewed"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            type="link"
                                            icon={<EyeOutlined />}
                                            size="small"
                                            className={doc.reviewed ? "text-green-600" : "text-gray-400"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewDocument(doc);
                                            }}>
                                            View
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Complete Your Information Section */}
                    <div className="px-4">
                        <div className="flex items-center justify-between my-4">
                            <Checkbox
                                checked={agreed}
                                onChange={handleAgreementChange}
                                disabled={loading}
                                className="text-md font-medium"
                                rules={[{ required: true, message: "Please agree to the terms and conditions" }]}>
                                I agree to the terms and conditions
                            </Checkbox>
                        </div>

                        <Form form={form} layout="vertical" requiredMark="optional">
                            <div className="grid grid-cols-2 gap-2">
                                <Form.Item
                                    label="Date of Agreement"
                                    name="date"
                                    rules={[{ required: true, message: "Please select a date" }]}>
                                    <DatePicker
                                        className="w-full"
                                        format="MM/DD/YYYY"
                                        placeholder="mm/dd/yyyy"
                                        disabled={loading}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label="Full Name"
                                    name="fullName"
                                    rules={[{ required: true, message: "Please enter your full name" }]}>
                                    <Input placeholder="Enter your full name" disabled={loading} />
                                </Form.Item>

                                <Form.Item
                                    label="Email Address"
                                    name="email"
                                    rules={[
                                        { required: true, message: "Please enter your email address" },
                                        { type: "email", message: "Please enter a valid email address" },
                                    ]}>
                                    <Input placeholder="your@email.com" disabled={loading} />
                                </Form.Item>

                                <Form.Item
                                    label="Contact Number"
                                    name="contactNumber"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please input your phone number!",
                                        },
                                        {
                                            min: 10,
                                            message: "Please input valid phone number!",
                                        },
                                    ]}>
                                    <PhoneInput country={"us"} enableSearch={true} disabled={loading} />
                                </Form.Item>
                            </div>

                            <div className="col-span-2">
                                <label className="block text-md mb-2">
                                    Digital Signature (As per Insurance Policy) <span className="text-red-500">*</span>
                                </label>
                                <SignaturePad
                                    onSave={handleSignatureSave}
                                    onClear={handleSignatureClear}
                                    width={600}
                                    t={(key) => {
                                        const translations = {
                                            clear: "Clear",
                                            signHere: "Sign here",
                                        };
                                        return translations[key] || key;
                                    }}
                                />
                                <div className="min-h-[18px] text-xs text-red-500 mt-1">
                                    {!signatureFile ? "Please provide your signature" : null}
                                </div>
                            </div>

                            <div className="col-span-2 text-xs text-gray-500">
                                By typing your name, you agree to the terms outlined in the documents above
                            </div>
                        </Form>
                    </div>

                    {/* Footer Buttons */}
                    <div className="flex justify-end gap-3 mt-4 px-4">
                        <Button onClick={handleCancel} disabled={loading} shape="round">
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={loading}
                            disabled={!agreed || !signatureFile || loading}
                            shape="round"
                            icon={<CheckCircleOutlined />}>
                            Complete Connection
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* PDF Viewer Modal */}
            <Modal
                open={pdfModalOpen}
                onCancel={handleClosePdfModal}
                footer={null}
                width={900}
                centered
                title={
                    <div className="flex items-center gap-2">
                        <FileTextOutlined className="text-blue-600" />
                        <span>{selectedDocument?.title}</span>
                    </div>
                }>
                <div className="w-full" style={{ height: "70vh" }}>
                    {selectedDocument?.pdfUrl ? (
                        <iframe
                            src={selectedDocument.pdfUrl}
                            title={selectedDocument.title}
                            className="w-full h-full border-0"
                            style={{ minHeight: "500px" }}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                            <p>No PDF available for this document</p>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

TeqMobilityConnectionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

export default TeqMobilityConnectionModal;
